import { getLastKnownAuthUser } from "../services/authService";
import { getDeviceId } from "../services/deviceService";
import { hasLocalPushSubscription } from "../services/pushNotificationService";
import { getCachedEmailRecipients, cacheEmailRecipients } from "../services/settingsService";
import { classifyRequestFailure, fetchWithTimeout } from "../utils/fetchWithTimeout";
import { getAllLocalWithdrawals } from "../storage/offlineDatabase";
import { fetchIncompleteServerSyncs } from "../services/syncRecoveryService";
import { getPendingAdminImports } from "../services/pendingAdminImportService";
import { getPendingMasterDataMutations } from "../services/pendingMasterDataDraftService";
import { recordAppUpdateHistory } from "../services/noticeHistoryService";
import { clearOperationalAdminHistoryState, flushPendingOperationalAdminHistory, recordOperationalAdminHistory } from "../services/operationalNoticeHistoryService";
import { formatBytes, runLocalStorageLifecycleMaintenance } from "../services/localStorageLifecycleService";
import { hasMasterDataUpdate, isLocalBootstrapVersion, isMasterDataCurrent } from "../utils/masterDataVersion";
import {
  clearCompletedMaterialentnahmeAppUpdate,
  dismissMaterialentnahmeAppUpdateNotice,
  getAppUpdateState,
  getCompletedMaterialentnahmeAppUpdate,
  subscribeAppUpdate,
} from "../services/serviceWorkerService";
import {
  getCachedAllocationMasterData,
  getAllocationMasterServerStatus,
} from "../services/allocationMasterDataService";
import { getCachedStorageOwnershipData, getStorageOwnershipServerStatus } from "../services/storageOwnershipDataService";
import {
  getLoadedMaterialMasterData,
  getMaterialMasterServerStatus,
  getCachedMaterialMasterData,
} from "../services/materialMasterDataService";
import {
  fetchVisionRecognitionServerStatus,
  readCachedActiveVisualLabProfile,
  visionProductionRuntimeDisplay,
  visionRuntimeCompatibility,
} from "../services/visualLabService";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createSequentialPoller } from "../utils/sequentialPoller.mjs";
import { useSingleFlightTask } from "../hooks/useSingleFlightTask";
import { AppStatusContext } from "./appStatusContextCore";
import type {
  AppQueueEntry,
  AppServiceId,
  AppServiceState,
  AppServiceStatus,
  AppStatusContextValue,
  AppStatusIssue,
  AppVisionOptimizerStatus,
} from "./appStatusTypes";

const DEFAULT_SERVICES: AppServiceStatus[] = [
  { id: "internetConnection", label: "Internetverbindung", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "backendApi", label: "Server", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "database", label: "Datenbank", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "emailWorker", label: "E-Mail-Worker", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "smtp", label: "SMTP-Verbindung", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "emailRecipient", label: "E-Mail-Empfänger", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "fileStorage", label: "Dateiablage", state: "unknown", detail: "Noch nicht geprüft", updatedAt: null },
  { id: "masterData", label: "Materialstammliste", state: "checking", detail: "Wird geprüft", updatedAt: null },
  { id: "allocationData", label: "Kontierungsliste", state: "checking", detail: "Wird geprüft", updatedAt: null },
  { id: "ownershipData", label: "Ordnungspatenschaften", state: "checking", detail: "Wird geprüft", updatedAt: null },
  { id: "visionProduction", label: "Etikettenerkennung", state: "checking", detail: "Wird geprüft", updatedAt: null },
];

const DISMISSED_VERSION_ISSUES_KEY = "materialentnahme:status:dismissed-version-issues:v1";

const PUSH_ONBOARDING_ISSUE_PREFIX = "push-onboarding:";
const PUSH_ONBOARDING_DISMISSED_PREFIX = "materialentnahme:status:push-onboarding-dismissed:v1:";

function pushOnboardingDismissed(issueId: string): boolean {
  try { return window.localStorage.getItem(`${PUSH_ONBOARDING_DISMISSED_PREFIX}${issueId}`) === "1"; } catch { return false; }
}

function rememberPushOnboardingDismissed(issueId: string): void {
  try { window.localStorage.setItem(`${PUSH_ONBOARDING_DISMISSED_PREFIX}${issueId}`, "1"); } catch { /* optional */ }
}

function getDismissedVersionIssueIds(): Set<string> {
  try {
    const raw = JSON.parse(window.localStorage.getItem(DISMISSED_VERSION_ISSUES_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((value): value is string => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

function rememberDismissedVersionIssue(id: string): void {
  const dismissed = getDismissedVersionIssueIds();
  dismissed.add(id);
  try { window.localStorage.setItem(DISMISSED_VERSION_ISSUES_KEY, JSON.stringify([...dismissed])); } catch { /* optional */ }
}

type InternetConnectionState = "online" | "slow" | "offline";

type RemoteDataVersionSnapshot = {
  available?: boolean;
  version?: {
    id: number; version_number: number; source_filename: string | null; source_hash: string; source_date: string | null; activated_at: string | null;
  } | null;
  manual_revision?: string | null;
};

type RemoteVersionInfo = {
  id: number; versionNumber: number; sourceFilename: string | null; sourceHash: string; sourceDate: string | null; activatedAt: string | null; manualRevision: string | null;
};

type SystemStatusPollSnapshot = {
  services?: Array<{ key:string; status:string; message:string; last_checked_at?:string; details?:{count?:number} }>;
  current_user_role?: string | null;
  data_versions?: { material?: RemoteDataVersionSnapshot; allocation?: RemoteDataVersionSnapshot; ownership?: RemoteDataVersionSnapshot };
  recognition?: {
    mode:string; recognition_revision?:number|null; bundle_id?:string|null; bundle_version?:string|null; activated_at?:string|null; profile_id?:number|null; profile_name?:string|null; integrity_status?:string|null; integrity_checked_at?:string|null; integrity_reasons?:string[]; fallback_active?:boolean;
  } | null;
  vision_optimizer?: AppVisionOptimizerStatus | null;
};

function mapRemoteDataVersion(snapshot: RemoteDataVersionSnapshot | undefined): RemoteVersionInfo | null | undefined {
  if (snapshot === undefined) return undefined;
  const version = snapshot.version;
  if (!version) return null;
  return {
    id: version.id, versionNumber: version.version_number, sourceFilename: version.source_filename,
    sourceHash: version.source_hash, sourceDate: version.source_date, activatedAt: version.activated_at,
    manualRevision: snapshot.manual_revision ?? null,
  };
}

async function checkInternetConnection(): Promise<InternetConnectionState> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  try {
    // Production-CSP bleibt bewusst auf connect-src 'self'. Die Konnektivitäts-
    // probe muss deshalb same-origin sein und darf nicht von Google/Microsoft
    // oder einem anderen Drittanbieter abhängen. Caddy beantwortet diesen Pfad
    // direkt mit 204; der Backend-Status bleibt davon vollständig getrennt.
    await fetchWithTimeout("/connectivity-check", { method: "GET", cache: "no-store" }, 1800);
    return "online";
  } catch (error) {
    const kind = classifyRequestFailure(error);
    // Ein Timeout beweist kein Offline-Szenario. Nur ein echter Netzwerkfehler
    // zusammen mit navigator.onLine=true wird als offline gewertet.
    return kind === "network" ? "offline" : "slow";
  }
}

function mapServiceState(status: string): AppServiceState {
  if (status === "ok") return "ok";
  if (status === "warning") return "warning";
  if (status === "error") return "error";
  return "unknown";
}

type EmailQueueResponseItem = {
  job_id:number; withdrawal_id:number; withdrawal_number:number|null; username?:string|null; status:string; material_number:string; withdrawal_date:string; material_text:string; attempt_count:number; max_attempts:number; queued_at:string; last_attempt_at:string|null; next_attempt_at:string|null; failed_at:string|null; last_error_user:string|null;
};

function mapEmailQueueEntries(queue: EmailQueueResponseItem[], isAdmin: boolean): AppQueueEntry[] {
  return queue.map((item) => ({
    id:`email:${item.job_id}`,
    state:item.status === "failed" ? "failed" : item.status === "retry_wait" || item.status === "processing" ? "retrying" : item.status === "sent" ? "sent" : "pending",
    title:`${isAdmin && item.username ? `${item.username} · ` : ""}#${item.withdrawal_number ?? "–"} | ${item.material_number} · ${item.material_text}`,
    withdrawalId:item.withdrawal_id,
    withdrawalDate:item.withdrawal_date,
    diagnosticTest:item.material_number === "9999999999" && item.material_text.startsWith("DIAGNOSE-SYNC-TEST"),
    detail:item.last_error_user,
    kind:"email",
    attempts:item.attempt_count,
    maxAttempts:item.max_attempts,
    nextAttemptAt:item.next_attempt_at,
    updatedAt:item.last_attempt_at ?? item.next_attempt_at ?? item.failed_at ?? item.queued_at,
  }));
}

async function getAuthoritativeServerRole(): Promise<string | null> {
  try {
    const response = await fetchWithTimeout(
      "/api/auth/me",
      { credentials: "include", cache: "no-store", headers: { Accept: "application/json" } },
      1200,
    );
    if (!response.ok) return null;
    const payload = await response.json() as { role?: unknown };
    return typeof payload.role === "string" ? payload.role : null;
  } catch {
    return null;
  }
}

function dataStandLabel(version: { versionNumber?: number | null; sourceDate?: string | null; activatedAt?: string | null; manualRevision?: string | null } | null | undefined): string {
  if (!version) return "Stand unbekannt";
  const versionText = version.versionNumber == null ? "Version –" : `Version ${version.versionNumber}`;
  const rawDate = version.sourceDate ?? version.activatedAt ?? null;
  const date = rawDate ? new Date(rawDate) : null;
  const stand = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" }) : "–";
  const revision = version.manualRevision ? " · geändert" : "";
  return `${versionText}${revision} · Stand ${stand}`;
}

export type ServerSessionState = "checking" | "confirmed" | "unavailable" | "rejected";

export function AppStatusProvider({ children, serverSessionState = "confirmed" }: { children: ReactNode; serverSessionState?: ServerSessionState }) {
  const protectedRemoteAllowed = serverSessionState === "confirmed";
  const [activity, setActivityState] = useState<string | null>(null);
  const [issues, setIssues] = useState<AppStatusIssue[]>([]);
  const [services, setServices] = useState<AppServiceStatus[]>(DEFAULT_SERVICES);
  const [queueEntries, setQueueEntriesState] = useState<AppQueueEntry[]>([]);
  const [visionOptimizer, setVisionOptimizer] = useState<AppVisionOptimizerStatus | null>(null);
  const backendLastReachableAtRef = useRef<string | null>(null);
  const backendLastConfirmedStateRef = useRef<"reachable" | "offline" | null>(null);

  const setActivity = useCallback((nextActivity: string | null) => setActivityState(nextActivity), []);
  const reportIssue = useCallback((issue: Omit<AppStatusIssue, "createdAt">) => {
    if ((issue.id.startsWith("master-update:") || issue.id.startsWith("allocation-update:") || issue.id.startsWith("ownership-update:")) && getDismissedVersionIssueIds().has(issue.id)) return;
    setIssues((current) => {
      const index = current.findIndex((item) => item.id === issue.id);
      const createdAt = index >= 0 ? current[index].createdAt : new Date().toISOString();
      const nextIssue = { ...issue, createdAt };
      return index < 0
        ? [...current, nextIssue]
        : current.map((item, i) => i === index ? nextIssue : item);
    });
  }, []);
  const clearIssue = useCallback((id: string) => {
    if (id.startsWith("master-update:") || id.startsWith("allocation-update:") || id.startsWith("ownership-update:")) rememberDismissedVersionIssue(id);
    if (id.startsWith("app-update:")) dismissMaterialentnahmeAppUpdateNotice();
    if (id.startsWith("app-updated:")) void clearCompletedMaterialentnahmeAppUpdate();
    if (id.startsWith(PUSH_ONBOARDING_ISSUE_PREFIX)) rememberPushOnboardingDismissed(id);
    setIssues((current) => current.filter((item) => item.id !== id));
    if (protectedRemoteAllowed && id.startsWith("server-notice-")) {
      const noticeId = Number(id.slice("server-notice-".length));
      if (Number.isFinite(noticeId)) {
        void fetchWithTimeout(`/api/notices/${noticeId}/resolve`, {
          method: "POST",
          credentials: "include",
        }, 10_000).catch(() => undefined);
      }
    }
  }, [protectedRemoteAllowed]);
  const clearAllIssues = useCallback(() => {
    if (issues.some((item) => item.id.startsWith("app-update:"))) dismissMaterialentnahmeAppUpdateNotice();
    if (issues.some((item) => item.id.startsWith("app-updated:"))) void clearCompletedMaterialentnahmeAppUpdate();
    for (const issue of issues) {
      if (issue.id.startsWith("master-update:") || issue.id.startsWith("allocation-update:") || issue.id.startsWith("ownership-update:")) {
        rememberDismissedVersionIssue(issue.id);
      }
      if (issue.id.startsWith(PUSH_ONBOARDING_ISSUE_PREFIX)) rememberPushOnboardingDismissed(issue.id);
    }
    const serverNoticeIds = issues
      .filter((item) => item.id.startsWith("server-notice-"))
      .map((item) => Number(item.id.slice("server-notice-".length)))
      .filter((noticeId) => Number.isFinite(noticeId));

    setIssues([]);

    if (!protectedRemoteAllowed) return;
    for (const noticeId of serverNoticeIds) {
      void fetchWithTimeout(`/api/notices/${noticeId}/resolve`, {
        method: "POST",
        credentials: "include",
      }, 10_000).catch(() => undefined);
    }
  }, [issues, protectedRemoteAllowed]);
  useEffect(() => {
    let cancelled = false;
    let timer = 0;

    const refreshPushOnboarding = async () => {
      const user = getLastKnownAuthUser();
      if (!user || !("Notification" in window) || !("PushManager" in window) || !("serviceWorker" in navigator)) return;
      const issueId = `${PUSH_ONBOARDING_ISSUE_PREFIX}${user.id}:${getDeviceId()}`;
      if (pushOnboardingDismissed(issueId)) {
        setIssues((current) => current.filter((item) => item.id !== issueId));
        return;
      }
      const active = await hasLocalPushSubscription().catch(() => false);
      if (cancelled) return;
      if (active) {
        // Eine erfolgreiche Aktivierung beweist, dass der Hinweis auf diesem Gerät
        // verstanden wurde. Nach späterer Deaktivierung wird er daher nicht erneut
        // eingeblendet; ein neues Gerät erhält wegen seiner anderen Device-ID einen
        // eigenen Hinweis.
        rememberPushOnboardingDismissed(issueId);
        setIssues((current) => current.filter((item) => item.id !== issueId));
        return;
      }
      reportIssue({
        id: issueId,
        severity: "info",
        title: "Systembenachrichtigungen aktivieren",
        detail: "Auf diesem Gerät sind Systembenachrichtigungen noch nicht aktiviert. Du kannst sie unter Einstellungen → Systembenachrichtigungen einschalten.",
        actionLabel: "Einstellungen öffnen",
        actionEvent: "materialentnahme:open-push-settings",
      });
    };

    timer = window.setTimeout(() => { void refreshPushOnboarding(); }, 450);
    const handleRefresh = () => { void refreshPushOnboarding(); };
    window.addEventListener("materialentnahme:push-status-changed", handleRefresh);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("materialentnahme:push-status-changed", handleRefresh);
    };
  }, [reportIssue]);

  const setServiceStatus = useCallback((id: AppServiceId, state: AppServiceState, detail: string) => {
    setServices((current) => current.map((service) => service.id === id
      ? { ...service, state, detail, updatedAt: new Date().toISOString() }
      : service));
  }, []);
  const setQueueEntries = useCallback((entries: AppQueueEntry[]) => setQueueEntriesState(entries), []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = 0;

    const syncCompletedAppUpdate = async () => {
      const completed = await getCompletedMaterialentnahmeAppUpdate();
      if (cancelled || !completed) return;
      const issueId = `app-updated:${completed.toVersion}`;
      setIssues((current) => current.some((item) => item.id === issueId) ? current : [...current, {
        id: issueId,
        severity: "info",
        title: "App erfolgreich aktualisiert",
        detail: `Materialentnahme wurde erfolgreich auf Version v${completed.toVersion} aktualisiert.`,
        createdAt: completed.completedAt,
      }]);

      const recordUntilSuccessful = async () => {
        if (!protectedRemoteAllowed) return;
        const ok = await recordAppUpdateHistory({ fromVersion: completed.fromVersion, toVersion: completed.toVersion });
        if (!ok && !cancelled) retryTimer = window.setTimeout(recordUntilSuccessful, 10_000);
      };
      void recordUntilSuccessful();
    };

    void syncCompletedAppUpdate();
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [protectedRemoteAllowed]);

  useEffect(() => {
    const syncAppUpdateIssue = () => {
      const state = getAppUpdateState();
      const target = state.targetVersion ?? "verfügbar";
      const id = `app-update:${target}`;
      setIssues((current) => {
        const withoutAppUpdates = current.filter((item) => !item.id.startsWith("app-update:"));
        if (!state.available || state.noticeDismissed) return withoutAppUpdates;
        return [...withoutAppUpdates, {
          id,
          severity: "info",
          title: state.restartRequired ? "App-Neustart erforderlich" : state.coordinatedByOtherTab ? "App-Update wird vorbereitet" : "Neue App Version verfügbar",
          detail: state.restartRequired
            ? `Der neue Service Worker${state.targetVersion ? ` v${state.targetVersion}` : ""} ist bereits aktiv. Dieser Tab bleibt bis zum kontrollierten Neuladen auf seinem geladenen App-Build.`
            : state.coordinatedByOtherTab
              ? `Version ${state.targetVersion ?? "neu"} wird bereits in einem anderen Tab vorbereitet. Dieser Tab wird nicht automatisch neu geladen.`
              : state.targetVersion ? `Version ${state.targetVersion} steht zur Aktualisierung bereit.` : "Eine neue App-Version steht zur Aktualisierung bereit.",
          createdAt: new Date().toISOString(),
        }];
      });
    };
    const unsubscribe = subscribeAppUpdate(syncAppUpdateIssue);
    const frame = window.requestAnimationFrame(syncAppUpdateIssue);
    return () => {
      window.cancelAnimationFrame(frame);
      unsubscribe();
    };
  }, []);

  const refreshSystemStatusImpl = useCallback(async (): Promise<void> => {
    const remoteServices: Array<{ key: string; id: AppServiceId }> = [
      { key: "database", id: "database" },
      { key: "email_worker", id: "emailWorker" },
      { key: "smtp", id: "smtp" },
      { key: "email_recipients", id: "emailRecipient" },
      { key: "file_storage", id: "fileStorage" },
    ];

    const internetState = await checkInternetConnection();
    setServiceStatus(
      "internetConnection",
      internetState === "online" ? "ok" : internetState === "slow" ? "warning" : "error",
      internetState === "online"
        ? "Internet erreichbar"
        : internetState === "slow"
          ? "Internetprüfung verzögert · Verbindung kann weiterhin bestehen"
          : "Keine Internetverbindung bestätigt",
    );

    let backendReachable = false;
    let backendFailure: unknown = null;
    let backendFailureKind: ReturnType<typeof classifyRequestFailure> = "unknown";

    // Ein einzelner kurzer Healthcheck-Aussetzer darf den bestätigten Serverzustand
    // nicht auf „offline“ kippen. Besonders wichtig: Ein Timeout beweist nur, dass
    // innerhalb unseres Zeitfensters keine Antwort kam. Er beweist NICHT, dass der
    // Server nicht erreichbar ist. Erst echte Netzwerkfehler werden nach Gegenprobe
    // als „nicht erreichbar“ gewertet. Jede HTTP-Antwort beweist Erreichbarkeit.
    for (const attempt of [1, 2] as const) {
      try {
        const response = await fetchWithTimeout(
          "/api/status",
          { credentials: "include", cache: "no-store" },
          attempt === 1 ? 1800 : 3200,
        );
        backendReachable = true;
        backendFailure = null;
        backendFailureKind = "unknown";
        backendLastReachableAtRef.current = new Date().toISOString();
        backendLastConfirmedStateRef.current = "reachable";
        if (!response.ok) {
          setServiceStatus("backendApi", "warning", `Server erreichbar · HTTP ${response.status}`);
        } else {
          setServiceStatus("backendApi", "ok", "API erreichbar");
        }
        clearIssue("backend-unavailable");
        break;
      } catch (error: unknown) {
        backendFailure = error;
        backendFailureKind = classifyRequestFailure(error);
        if (attempt === 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 350));
          continue;
        }
      }
    }

    if (!backendReachable) {
      const timedOut = backendFailureKind === "timeout";
      const lastReachableAt = backendLastReachableAtRef.current;
      const lastReachable = lastReachableAt
        ? ` · zuletzt erreichbar ${new Date(lastReachableAt).toLocaleTimeString("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
        : "";

      if (timedOut) {
        // Ein Timeout verändert keinen zuvor bestätigten Offline-/Online-Zustand.
        // War der Server zuletzt erreichbar (oder noch nie bestätigt offline), ist
        // das nur eine Warnung. War er zuvor wirklich offline, bleibt dieser
        // bestätigte Fehler erhalten, bis eine echte HTTP-Antwort ihn widerlegt.
        if (backendLastConfirmedStateRef.current === "offline") {
          setServiceStatus("backendApi", "error", `Zuletzt nicht erreichbar · aktuelle Prüfung verzögert${lastReachable}`);
        } else {
          setServiceStatus("backendApi", "warning", `Antwort verzögert${lastReachable}`);
          clearIssue("backend-unavailable");
        }
        for (const { id } of remoteServices) {
          if (id === "emailRecipient") continue;
          setServiceStatus(id, "unknown", "Nicht prüfbar · Serverantwort zu langsam");
        }
      } else {
        const detail = backendFailure instanceof Error ? backendFailure.message : "Netzwerkverbindung zum Server fehlgeschlagen.";
        backendLastConfirmedStateRef.current = "offline";
        setServiceStatus("backendApi", "error", "Nicht erreichbar");
        reportIssue({ id: "backend-unavailable", severity: "warning", title: "Server nicht erreichbar", detail });
        for (const { id } of remoteServices) {
          if (id === "emailRecipient") continue;
          setServiceStatus(id, "unknown", internetState !== "offline" ? "Nicht prüfbar · Server nicht erreichbar" : "Nicht prüfbar · Keine Internetverbindung");
        }
      }

      const cachedRecipients = getCachedEmailRecipients();
      if (cachedRecipients === null) {
        setServiceStatus("emailRecipient", "unknown", "Noch nicht bekannt");
      } else if (cachedRecipients.length > 0) {
        setServiceStatus("emailRecipient", "ok", `${cachedRecipients.length} E-Mail-Empfänger hinterlegt · lokaler Stand`);
      } else {
        setServiceStatus("emailRecipient", "warning", "Keine E-Mail-Empfänger hinterlegt · lokaler Stand");
      }
    }

    const protectedBackendReachable = backendReachable && protectedRemoteAllowed;
    let authInvalidatedThisCycle = false;
    const canUseProtectedRemote = () => protectedBackendReachable && !authInvalidatedThisCycle;
    if (backendReachable && !protectedRemoteAllowed) {
      const detail = serverSessionState === "checking" ? "Sitzung wird bestätigt" : serverSessionState === "unavailable" ? "Nur lokal · Server-Sitzung nicht bestätigt" : "Nicht angemeldet";
      for (const { id } of remoteServices) setServiceStatus(id, "unknown", detail);
    }

    // Der gemeinsame Systemstatus liefert die authoritative Rolle sowie die
    // globalen Masterdata-/Recognition-Metadaten gleich mit. Damit entfallen pro
    // Statuszyklus vier weitere authentifizierte Statusrequests + normalerweise
    // der separate /auth/me-Rollenrequest.
    let systemSnapshot: SystemStatusPollSnapshot | null = null;
    let authoritativeRole: string | null = null;
    const resolveServerRole = async (): Promise<string | null> => {
      if (authoritativeRole !== null) return authoritativeRole;
      if (!canUseProtectedRemote()) return null;
      authoritativeRole = await getAuthoritativeServerRole().catch(() => null);
      return authoritativeRole;
    };

    const serviceTask = async () => {
      if (!canUseProtectedRemote()) return;
      try {
        const response = await fetchWithTimeout("/api/system/status", { credentials: "include", cache: "no-store" }, 5200);
        if (response.status === 401) {
          authInvalidatedThisCycle = true;
          window.dispatchEvent(new Event("materialentnahme:auth-invalid"));
          return;
        }
        if (!response.ok) throw new Error(`system status ${response.status}`);
        const result = await response.json() as SystemStatusPollSnapshot;
        systemSnapshot = result;
        authoritativeRole = typeof result.current_user_role === "string" ? result.current_user_role : null;
        if (result.vision_optimizer !== undefined) setVisionOptimizer(result.vision_optimizer ?? null);
        const byKey = new Map((result.services ?? []).map((service) => [service.key, service]));
        for (const { key, id } of remoteServices) {
          const service = byKey.get(key);
          if (!service) { setServiceStatus(id, "unknown", "Noch nicht geprüft"); continue; }
          setServices((current) => current.map((entry) => entry.id === id ? {
            ...entry, state: mapServiceState(service.status), detail: service.message, updatedAt: service.last_checked_at ?? new Date().toISOString(),
          } : entry));
          if (id === "emailRecipient" && typeof service.details?.count === "number") {
            const existing = getCachedEmailRecipients();
            if (service.details.count === 0) cacheEmailRecipients([]);
            else if (existing === null || existing.length !== service.details.count) {
              try {
                const recipientResponse = await fetchWithTimeout("/api/settings/email-recipients", { credentials: "include", cache: "no-store" }, 1800);
                if (recipientResponse.ok) cacheEmailRecipients(await recipientResponse.json());
              } catch { /* Der gemeinsame Systemstatus selbst ist bereits bekannt. */ }
            }
          }
        }
      } catch {
        for (const { id } of remoteServices) setServiceStatus(id, "unknown", "Nicht prüfbar");
      }
    };

    const queueTask = async () => {
      try {
        const authUser = getLastKnownAuthUser();
        const serverRole = await resolveServerRole();
        const isAdmin = serverRole === "admin";
        const localRecords = await getAllLocalWithdrawals();
        const localEntries: AppQueueEntry[] = localRecords
          .filter((record) => record.sync_status !== "synced" && (!authUser || record.owner_user_id === authUser.id))
          .map((record) => ({
            id:`sync:${record.client_uuid}`,
            state:record.deleted_at ? (record.sync_status === "syncing" ? "retrying" : "pending") : (record.sync_attempt_count ?? 0) >= 9 ? "failed" : record.sync_status === "error" ? "failed" : record.sync_status === "syncing" ? "retrying" : "pending",
            title:`#${record.local_withdrawal_number ?? record.server_withdrawal_number ?? "–"} | ${record.data.material_number} · ${record.data.material_text}`,
            withdrawalId:record.server_withdrawal_id,
            clientUuid:record.client_uuid,
            withdrawalDate:record.data.withdrawal_date,
            diagnosticTest: record.data.material_number === "9999999999" && record.data.material_text.startsWith("DIAGNOSE-SYNC-TEST"),
            detail:record.deleted_at ? "Gelöscht · Löschstatus wird mit dem Server abgeglichen" : (record.sync_attempt_count ?? 0) >= 9 ? (record.last_error ?? "Automatische Synchronisierung beendet · Aktion erforderlich") : record.last_error ?? (record.sync_status === "pending" ? "Synchronisierung ausstehend" : "Synchronisierung läuft"),
            kind:"sync",
            attempts:record.sync_attempt_count ?? (record.last_sync_attempt_at ? 1 : 0),
            maxAttempts:9,
            nextAttemptAt:record.next_sync_attempt_at ?? null,
            updatedAt:record.last_sync_attempt_at ?? record.updated_at,
          }));

        // Lokale Sync-Vorgänge sind unabhängig vom Backend sichtbar. Dadurch wird
        // ein gerade laufender Sync sofort unter „Versand & Verlauf“ angezeigt.
        if (!canUseProtectedRemote()) {
          setQueueEntriesState(localEntries);
          return;
        }

        let emailEntries: AppQueueEntry[] | null = null;
        try {
          const response = await fetchWithTimeout(
            isAdmin ? "/api/email/admin/queue" : "/api/email/queue",
            { credentials: "include", cache: "no-store" },
            1800,
          );
          if (response.ok) {
            emailEntries = mapEmailQueueEntries(await response.json() as EmailQueueResponseItem[], isAdmin);
          }
        } catch { /* letzter E-Mail-Queue-Stand bleibt bis zur nächsten autoritativen Antwort sichtbar */ }

        const localByUuid = new Map(localRecords.map((record) => [record.client_uuid, record]));
        let serverSyncEntries: AppQueueEntry[] = [];
        try {
          const incompleteServer = await fetchIncompleteServerSyncs();
          const localEntryUuids = new Set(localEntries.map((entry) => entry.id.replace(/^sync:/, "")));
          serverSyncEntries = incompleteServer.filter((item) => !localEntryUuids.has(item.uuid)).map((item) => {
            const local = localByUuid.get(item.uuid) ?? null;
            const localRecoverable = Boolean(local?.pdf_blob && local.record_type !== "draft");
            return { id:`sync:${item.uuid}`, kind:"sync" as const, diagnosticTest:item.material_number === "9999999999" && item.material_text.startsWith("DIAGNOSE-SYNC-TEST"), state:(local?.sync_attempt_count ?? 0) >= 9 ? "failed" as const : localRecoverable ? "retrying" as const : "failed" as const, title:`#${item.withdrawal_number ?? "–"} | ${item.material_number} · ${item.material_text}`, withdrawalId:item.id, clientUuid:item.uuid, withdrawalDate:item.withdrawal_date, detail:localRecoverable ? "Server-Synchronisierung unvollständig · lokale Dokumente für Recovery vorhanden" : item.problem_detail ?? "Server-Synchronisierung unvollständig · lokale Recovery-Daten fehlen", attempts:local?.sync_attempt_count ?? 0, maxAttempts:9, nextAttemptAt:local?.next_sync_attempt_at ?? null, updatedAt:local?.last_sync_attempt_at ?? item.updated_at };
          });
        } catch { /* optional */ }

        setQueueEntriesState((current) => {
          const effectiveEmailEntries = emailEntries ?? current.filter((entry) => entry.kind === "email");
          return [...localEntries, ...serverSyncEntries, ...effectiveEmailEntries];
        });

        // Nur Admins erhalten einen lokalen Hinweis, wenn ein offline vorgemerkter
        // Stammdatenimport jetzt wieder analysiert werden kann.
        if (isAdmin) {
          const pendingImports = await getPendingAdminImports().catch(() => []);
          const pendingMasterDrafts = await getPendingMasterDataMutations().catch(() => []);
          for (const kind of ["material", "allocation", "ownership"] as const) {
            const id = `pending-admin-import-${kind}`;
            const title = kind === "material" ? "Materialstammliste bereit zum Abgleich" : kind === "allocation" ? "Kontierungsliste bereit zum Abgleich" : "Ordnungspatenschaften bereit zum Abgleich";
            if (pendingImports.some((item) => item.kind === kind)) reportIssue({ id, severity: "info", title, detail: "Die lokal gespeicherte Importdatei kann jetzt im Admin-Menü analysiert werden." });
            else clearIssue(id);

            const draftId = `pending-master-draft-${kind}`;
            const localDrafts = pendingMasterDrafts.filter((item) => item.kind === kind);
            const conflicts = localDrafts.filter((item) => item.sync_state === "conflict").length;
            if (localDrafts.length) reportIssue({
              id: draftId,
              severity: conflicts ? "warning" : "info",
              title: conflicts ? "Lokale Stammdatenänderung mit Konflikt" : "Lokale Stammdatenänderung wartet auf Synchronisierung",
              detail: conflicts
                ? `${conflicts} von ${localDrafts.length} lokalen Änderungen benötigen einen erneuten Abgleich mit dem Server.`
                : `${localDrafts.length} Änderung${localDrafts.length === 1 ? "" : "en"} ist lokal in IndexedDB gespeichert und wird bei verfügbarer Serververbindung synchronisiert.`,
            });
            else clearIssue(draftId);
          }
        }
      } catch { /* alter sichtbarer Stand bleibt erhalten */ }
    };


    if (canUseProtectedRemote()) void flushPendingOperationalAdminHistory();

    const storageTask = async () => {
      try {
        const report = await runLocalStorageLifecycleMaintenance({
          allowNetworkVerification: canUseProtectedRemote(),
          reason: "status",
        });
        if (report.level === "critical") {
          reportIssue({
            id: "local-storage-budget",
            severity: "error",
            title: "Lokaler Speicher fast voll",
            detail: `${formatBytes(report.usageBytes)} lokal belegt · Schutzgrenze ${formatBytes(report.hardGuardBytes)}. `
              + `${formatBytes(report.protectedBytes)} lokale Vorgangsdateien sind geschützt und werden nicht automatisch gelöscht`
              + (report.reclaimableBytes > 0 ? ` · ${formatBytes(report.reclaimableBytes)} sicher bereinigbar.` : "."),
          });
          void recordOperationalAdminHistory({ kind:"storage_warning", title:"Lokaler Speicher fast voll", detail:`Kritischer lokaler Speicherzustand bei ${getLastKnownAuthUser()?.username ?? "Benutzer"}: ${formatBytes(report.usageBytes)} belegt · Hard Guard ${formatBytes(report.hardGuardBytes)}.`, event_key:"storage:critical" });
        } else if (report.level === "warning") {
          reportIssue({
            id: "local-storage-budget",
            severity: "warning",
            title: "Lokaler Speicher wird knapp",
            detail: `${formatBytes(report.usageBytes)} lokal belegt. `
              + `${formatBytes(report.reclaimableBytes)} sind nach bestätigtem Serverarchiv sicher bereinigbar · `
              + `${report.unsyncedCount} nicht synchronisierte Vorgänge bleiben geschützt.`,
          });
          void recordOperationalAdminHistory({ kind:"storage_warning", title:"Lokaler Speicher wird knapp", detail:`Hoher lokaler Speicherverbrauch bei ${getLastKnownAuthUser()?.username ?? "Benutzer"}: ${formatBytes(report.usageBytes)} belegt · ${formatBytes(report.reclaimableBytes)} sicher bereinigbar.`, event_key:"storage:warning" });
        } else {
          clearIssue("local-storage-budget");
          clearOperationalAdminHistoryState("storage_warning");
        }
      } catch {
        // Speicherdiagnose darf den restlichen Systemstatus niemals blockieren.
      }
    };

    const materialTask = async () => {
      if (!canUseProtectedRemote()) {
        const cached = await getCachedMaterialMasterData();
        setServiceStatus("masterData", cached ? "ok" : "error", cached ? `${dataStandLabel(cached.version)} · ${cached.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar` : "Nicht verfügbar");
        return;
      }
      try {
        const combinedVersion = mapRemoteDataVersion(systemSnapshot?.data_versions?.material);
        const serverVersion = combinedVersion !== undefined ? combinedVersion : await getMaterialMasterServerStatus();
        const localSnapshot = getLoadedMaterialMasterData() ?? await getCachedMaterialMasterData();
        const serverVersionNumber = serverVersion?.versionNumber ?? null;
        if (serverVersionNumber !== null && !localSnapshot) {
          setServiceStatus("masterData", "ok", `Version ${serverVersionNumber} · Server verfügbar`);
          setIssues((current) => current.filter((item) => !item.id.startsWith("master-update:")));
        } else if (serverVersionNumber !== null && localSnapshot && isLocalBootstrapVersion(localSnapshot.version)) {
          setServiceStatus("masterData", "checking", `${dataStandLabel(localSnapshot.version)} · Serverstand wird geladen`);
          setIssues((current) => current.filter((item) => !item.id.startsWith("master-update:")));
        } else if (serverVersionNumber !== null && localSnapshot && hasMasterDataUpdate(serverVersion, localSnapshot.version)) {
          setServiceStatus("masterData", "warning", `${dataStandLabel(localSnapshot.version)} · Neue Version verfügbar`);
          const targetKey = `${serverVersionNumber}:${serverVersion?.manualRevision ?? "base"}`;
          const revisionChanged = (serverVersion?.manualRevision ?? null) !== (localSnapshot.version.manualRevision ?? null);
          reportIssue({
            id: `master-update:${targetKey}`,
            severity: "info",
            title: "Neue Materialstammliste verfügbar",
            detail: revisionChanged && serverVersionNumber === localSnapshot.version.versionNumber
              ? `Version ${serverVersionNumber} wurde aktualisiert und kann übernommen werden.`
              : `Version ${serverVersionNumber} wurde freigegeben und kann aktualisiert werden.`,
          });
          setIssues((current) => current.filter((item) => !item.id.startsWith("master-update:") || item.id === `master-update:${targetKey}`));
        } else if (serverVersionNumber !== null && localSnapshot && isMasterDataCurrent(serverVersion, localSnapshot.version)) {
          setServiceStatus("masterData", "ok", `${dataStandLabel(localSnapshot.version)} · Aktuell`);
          setIssues((current) => current.filter((item) => !item.id.startsWith("master-update:")));
        }
        clearIssue("master-error");
      } catch {
        const cached = await getCachedMaterialMasterData();
        setServiceStatus("masterData", cached ? "ok" : "error", cached ? `${dataStandLabel(cached.version)} · ${cached.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar` : "Nicht verfügbar");
      }
    };

    const allocationTask = async () => {
      if (!canUseProtectedRemote()) {
        const cached = await getCachedAllocationMasterData();
        setServiceStatus("allocationData", cached ? "ok" : "error", cached ? `${dataStandLabel(cached.version)} · ${cached.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar` : "Nicht verfügbar");
        return;
      }
      try {
        const combinedVersion = mapRemoteDataVersion(systemSnapshot?.data_versions?.allocation);
        const serverVersion = combinedVersion !== undefined ? combinedVersion : await getAllocationMasterServerStatus();
        const localSnapshot = await getCachedAllocationMasterData();
        const serverVersionNumber = serverVersion?.versionNumber ?? null;
        if (serverVersionNumber !== null && !localSnapshot) {
          setServiceStatus("allocationData", "ok", `Version ${serverVersionNumber} · Server verfügbar`);
          setIssues((current) => current.filter((item) => !item.id.startsWith("allocation-update:")));
        } else if (serverVersionNumber !== null && localSnapshot && isLocalBootstrapVersion(localSnapshot.version)) {
          setServiceStatus("allocationData", "checking", `${dataStandLabel(localSnapshot.version)} · Serverstand wird geladen`);
          setIssues((current) => current.filter((item) => !item.id.startsWith("allocation-update:")));
        } else if (serverVersionNumber !== null && localSnapshot && hasMasterDataUpdate(serverVersion, localSnapshot.version)) {
          setServiceStatus("allocationData", "warning", `${dataStandLabel(localSnapshot.version)} · Neue Version verfügbar`);
          const targetKey = `${serverVersionNumber}:${serverVersion?.manualRevision ?? "base"}`;
          const revisionChanged = (serverVersion?.manualRevision ?? null) !== (localSnapshot.version.manualRevision ?? null);
          reportIssue({
            id: `allocation-update:${targetKey}`,
            severity: "info",
            title: "Neue Kontierungsliste verfügbar",
            detail: revisionChanged && serverVersionNumber === localSnapshot.version.versionNumber
              ? `Version ${serverVersionNumber} wurde aktualisiert und kann übernommen werden.`
              : `Version ${serverVersionNumber} wurde freigegeben und kann aktualisiert werden.`,
          });
          setIssues((current) => current.filter((item) => !item.id.startsWith("allocation-update:") || item.id === `allocation-update:${targetKey}`));
        } else if (serverVersionNumber !== null && localSnapshot && isMasterDataCurrent(serverVersion, localSnapshot.version)) {
          setServiceStatus("allocationData", "ok", `${dataStandLabel(localSnapshot.version)} · Aktuell`);
          setIssues((current) => current.filter((item) => !item.id.startsWith("allocation-update:")));
        }
        clearIssue("allocation-error");
      } catch {
        const cached = await getCachedAllocationMasterData();
        setServiceStatus("allocationData", cached ? "ok" : "error", cached ? `${dataStandLabel(cached.version)} · ${cached.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar` : "Nicht verfügbar");
      }
    };

    const ownershipTask = async () => {
      if (!canUseProtectedRemote()) {
        const cached = await getCachedStorageOwnershipData();
        setServiceStatus("ownershipData", cached ? "ok" : "error", cached ? `${dataStandLabel(cached.version)} · ${cached.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar` : "Nicht verfügbar");
        return;
      }
      try {
        const combinedVersion = mapRemoteDataVersion(systemSnapshot?.data_versions?.ownership);
        const serverVersion = combinedVersion !== undefined ? combinedVersion : await getStorageOwnershipServerStatus();
        const localSnapshot = await getCachedStorageOwnershipData();
        const serverVersionNumber = serverVersion?.versionNumber ?? null;
        if (serverVersionNumber !== null && !localSnapshot) {
          setServiceStatus("ownershipData", "ok", `Version ${serverVersionNumber} · Server verfügbar`);
          setIssues((current)=>current.filter((item)=>!item.id.startsWith("ownership-update:")));
        } else if (serverVersionNumber !== null && localSnapshot && isLocalBootstrapVersion(localSnapshot.version)) {
          setServiceStatus("ownershipData", "checking", `${dataStandLabel(localSnapshot.version)} · Serverstand wird geladen`);
          setIssues((current)=>current.filter((item)=>!item.id.startsWith("ownership-update:")));
        } else if (serverVersionNumber !== null && localSnapshot && hasMasterDataUpdate(serverVersion, localSnapshot.version)) {
          setServiceStatus("ownershipData", "warning", `${dataStandLabel(localSnapshot.version)} · Neue Version verfügbar`);
          const targetKey = `${serverVersionNumber}:${serverVersion?.manualRevision ?? "base"}`;
          const revisionChanged = (serverVersion?.manualRevision ?? null) !== (localSnapshot.version.manualRevision ?? null);
          reportIssue({ id:`ownership-update:${targetKey}`, severity:"info", title:"Neue Ordnungspatenschaften verfügbar", detail: revisionChanged && serverVersionNumber === localSnapshot.version.versionNumber ? `Version ${serverVersionNumber} wurde aktualisiert und kann übernommen werden.` : `Version ${serverVersionNumber} wurde freigegeben und kann aktualisiert werden.` });
          setIssues((current)=>current.filter((item)=>!item.id.startsWith("ownership-update:") || item.id===`ownership-update:${targetKey}`));
        } else if (serverVersionNumber !== null && localSnapshot && isMasterDataCurrent(serverVersion, localSnapshot.version)) {
          setServiceStatus("ownershipData", "ok", `${dataStandLabel(localSnapshot.version)} · Aktuell`);
          setIssues((current)=>current.filter((item)=>!item.id.startsWith("ownership-update:")));
        }
        clearIssue("ownership-error");
      } catch {
        const cached=await getCachedStorageOwnershipData();
        setServiceStatus("ownershipData",cached?"ok":"error",cached?`${dataStandLabel(cached.version)} · ${cached.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar`:"Nicht verfügbar");
      }
    };

    const recognitionTask = async () => {
      const cached = readCachedActiveVisualLabProfile();
      const localBundle = cached?.production_bundle ?? null;
      const localCompatibility = visionRuntimeCompatibility(localBundle);
      const localDisplay = cached ? visionProductionRuntimeDisplay(cached) : null;
      const localLabel = localDisplay?.recognitionRevision
        ? `Stand ${localDisplay.recognitionRevision}`
        : localBundle ? "Optimierter Stand" : "Standard-Erkennung";

      if (!canUseProtectedRemote()) {
        if (localBundle && !localCompatibility.compatible) {
          setServiceStatus("visionProduction", "warning", `Standard-Fallback aktiv · ${localLabel} nicht verwendbar`);
          void recordOperationalAdminHistory({ kind:"recognition_fallback", title:"Etikettenerkennung: Standard-Fallback aktiv", detail:`${localLabel} ist lokal nicht runtime-kompatibel. Die sichere Standard-Erkennung ist aktiv.`, event_key:`recognition:local-incompatible:${localBundle.bundle_id ?? "local"}` });
        } else {
          setServiceStatus("visionProduction", "ok", `${localLabel} · Lokal verfügbar`);
        }
        return;
      }

      try {
        const server = systemSnapshot?.recognition ?? await fetchVisionRecognitionServerStatus();
        const serverLabel = server.mode === "optimized"
          ? server.recognition_revision ? `Stand ${server.recognition_revision}` : "Optimierter Stand"
          : "Standard-Erkennung";
        const localBundleId = localBundle?.bundle_id ?? null;
        const localMode = localBundle ? "optimized" : "standard";
        const differs = server.mode !== localMode || (server.mode === "optimized" && server.bundle_id !== localBundleId);

        if (server.fallback_active) {
          setServiceStatus("visionProduction", "warning", "Standard-Fallback aktiv · Optimierter Stand nicht verfügbar");
          void recordOperationalAdminHistory({ kind:"recognition_fallback", title:"Etikettenerkennung: Standard-Fallback aktiv", detail:"Der administrativ optimierte Erkennungsstand konnte nicht verwendet werden. Die sichere Standard-Erkennung ist aktiv.", event_key:`recognition:fallback:${server.bundle_id ?? "optimized"}` });
          return;
        }
        if (!differs && localBundle && !localCompatibility.compatible) {
          setServiceStatus("visionProduction", "warning", `Standard-Fallback aktiv · ${localLabel} mit dieser PWA nicht verwendbar`);
          void recordOperationalAdminHistory({ kind:"recognition_fallback", title:"Etikettenerkennung: Standard-Fallback aktiv", detail:`${localLabel} ist mit dieser PWA nicht runtime-kompatibel. Die sichere Standard-Erkennung ist aktiv.`, event_key:`recognition:incompatible:${localBundle.bundle_id ?? "local"}` });
          return;
        }
        if (differs) {
          clearOperationalAdminHistoryState("recognition_fallback");
          setServiceStatus("visionProduction", "warning", `${serverLabel} · Neuer Stand verfügbar`);
          return;
        }
        clearOperationalAdminHistoryState("recognition_fallback");
        if (server.mode === "optimized") {
          setServiceStatus("visionProduction", "ok", `${serverLabel} · Aktuell`);
        } else {
          setServiceStatus("visionProduction", "ok", "Standard-Erkennung aktiv · Aktuell");
        }
      } catch {
        if (localBundle && !localCompatibility.compatible) {
          setServiceStatus("visionProduction", "warning", `Standard-Fallback aktiv · ${localLabel} nicht verwendbar`);
          void recordOperationalAdminHistory({ kind:"recognition_fallback", title:"Etikettenerkennung: Standard-Fallback aktiv", detail:`${localLabel} ist lokal nicht runtime-kompatibel. Die sichere Standard-Erkennung ist aktiv.`, event_key:`recognition:local-incompatible:${localBundle.bundle_id ?? "local"}` });
        } else if (cached) {
          setServiceStatus("visionProduction", "ok", `${localLabel} · Lokal verfügbar · Serverstatus unbekannt`);
        } else {
          setServiceStatus("visionProduction", "unknown", "Noch nicht geprüft");
        }
      }
    };

    const noticeTask = async () => {
      if (!canUseProtectedRemote()) return;
      try {
        const isAdmin = (await resolveServerRole()) === "admin";
        const publicResponse = await fetchWithTimeout("/api/notices", { credentials:"include", cache:"no-store" }, 1800);
        const adminResponse = isAdmin
          ? await fetchWithTimeout("/api/notices/admin", { credentials:"include", cache:"no-store" }, 1800)
          : null;
        type NoticePayload = { id:number; notice_type:string; dedupe_key:string; title:string; detail:string|null; occurrence_count:number; first_seen_at?:string; last_seen_at?:string; diagnostic_test?:boolean; severity?:"info"|"warning"|"error"; tone?:string; decision_key?:string|null; target_url?:string|null; admin_only?:boolean };
        const publicNotices: NoticePayload[] = publicResponse.ok ? await publicResponse.json() as NoticePayload[] : [];
        const adminNotices: NoticePayload[] = adminResponse?.ok ? await adminResponse.json() as NoticePayload[] : [];
        const [localMaterial, localAllocation, localOwnership] = await Promise.all([
          getCachedMaterialMasterData().catch(() => null),
          getCachedAllocationMasterData().catch(() => null),
          getCachedStorageOwnershipData().catch(() => null),
        ]);
        const releaseStillRelevant = (notice: NoticePayload): boolean => {
          if (notice.notice_type === "material_master_release") {
            const released = Number.parseInt(notice.dedupe_key, 10);
            const local = localMaterial?.version.versionNumber ?? null;
            return !Number.isFinite(released) || local === null || local < released;
          }
          if (notice.notice_type === "allocation_master_release") {
            const released = Number.parseInt(notice.dedupe_key, 10);
            const local = localAllocation?.version.versionNumber ?? null;
            return !Number.isFinite(released) || local === null || local < released;
          }
          if (notice.notice_type === "storage_ownership_release") {
            const released = Number.parseInt(notice.dedupe_key, 10);
            const local = localOwnership?.version.versionNumber ?? null;
            return !Number.isFinite(released) || local === null || local < released;
          }
          return true;
        };
        const noticeMap = new Map<string, NoticePayload & { adminOnly: boolean }>();
        const publicNoticeTypes = new Set(["material_master_release", "allocation_master_release", "storage_ownership_release", "system_info"]);
        const isActiveListReleaseNotice = (notice: NoticePayload) => notice.notice_type === "material_master_release" || notice.notice_type === "allocation_master_release" || notice.notice_type === "storage_ownership_release";
        const publicNoticeKeys = new Set<string>();
        for (const notice of publicNotices.filter(releaseStillRelevant).filter((notice) => !isActiveListReleaseNotice(notice))) {
          const key = `${notice.notice_type}:${notice.dedupe_key}`;
          publicNoticeKeys.add(key);
          noticeMap.set(key, { ...notice, adminOnly: false });
        }
        for (const notice of adminNotices.filter(releaseStillRelevant).filter((notice) => !isActiveListReleaseNotice(notice))) {
          const key = `${notice.notice_type}:${notice.dedupe_key}`;
          // Die eigene Handover-Erinnerung ist bereits in /api/notices eine normale
          // Benutzermeldung. Derselbe Typ für fremde Entnahmen bleibt für Admins
          // dagegen eine Admin-Meldung. Alle anderen allgemeinen Meldungstypen
          // behalten ihre bisherige Klassifizierung.
          const adminOnly = notice.notice_type === "withdrawal_handover_overdue"
            ? !publicNoticeKeys.has(key)
            : !publicNoticeTypes.has(notice.notice_type);
          noticeMap.set(key, { ...notice, adminOnly });
        }
        const unique = Array.from(noticeMap.values());
        const adminUnknownMaterialIssueIds = new Set(
          adminNotices
            .filter((notice) => notice.notice_type === "unknown_material")
            .map((notice) => `unknown-material-${notice.dedupe_key}`),
        );
        setIssues((current)=>[
          ...current.filter((item)=>!item.id.startsWith("server-notice-") && !adminUnknownMaterialIssueIds.has(item.id)),
          ...unique.map((notice):AppStatusIssue=>({
            id:`server-notice-${notice.id}`,
            severity:notice.severity ?? (notice.notice_type === "withdrawal_created" || notice.notice_type.endsWith("_release") || notice.notice_type === "system_info" || notice.notice_type === "device_registry_stale" || notice.notice_type === "withdrawal_handover_overdue" ? "info" : notice.notice_type === "service_failure" || (notice.notice_type === "server_storage_capacity" && notice.title.toLocaleLowerCase("de-DE").includes("kritisch")) ? "error" : "warning"),
            title:notice.title,
            detail:`${notice.detail ?? ""}${notice.notice_type !== "service_failure" && notice.occurrence_count>1?` · ${notice.occurrence_count}× erkannt`:""}`.trim(),
            adminOnly: notice.admin_only ?? notice.adminOnly,
            diagnosticTest: Boolean(notice.diagnostic_test),
            actionLabel: notice.target_url
              ? (notice.notice_type === "vision_optimizer" ? "Passende VO-Details öffnen" : "Details öffnen")
              : undefined,
            actionUrl: notice.target_url ?? undefined,
            decisionKey: notice.decision_key ?? notice.dedupe_key,
            createdAt:notice.first_seen_at ?? notice.last_seen_at ?? new Date().toISOString(),
          })),
        ]);
      } catch { /* Hinweise blockieren keine Statusbereiche. */ }
    };

    // Keine Status-Request-Fächer mehr: derselbe Tab arbeitet die Remote-/Local-
    // Statusquellen nacheinander ab. Zusammen mit dem globalen API-Request-Gate
    // verhindert das Wake-up-Bursts gegen Backend/DB, ohne Nutzeraktionen zu blocken.
    for (const task of [serviceTask, queueTask, materialTask, allocationTask, ownershipTask, recognitionTask, noticeTask, storageTask]) {
      try { await task(); } catch { /* Ein Teilstatus darf den Rest nicht blockieren. */ }
    }
  }, [clearIssue, protectedRemoteAllowed, reportIssue, serverSessionState, setServiceStatus]);
  const refreshSystemStatusSingleFlight = useSingleFlightTask(refreshSystemStatusImpl);
  const refreshSystemStatus = useCallback(async (): Promise<void> => {
    await refreshSystemStatusSingleFlight();
  }, [refreshSystemStatusSingleFlight]);

  useEffect(() => {
    let cancelled = false;

    void Promise.all([
      getCachedMaterialMasterData(),
      getCachedAllocationMasterData(),
      getCachedStorageOwnershipData(),
    ]).then(([material, allocation, ownership]) => {
      if (cancelled) return;

      if (material) {
        setServiceStatus(
          "masterData",
          "ok",
          `${dataStandLabel(material.version)} · ${material.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar`
        );
      }

      if (allocation) {
        setServiceStatus("allocationData", "ok", `${dataStandLabel(allocation.version)} · ${allocation.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar`);
      }
      if (ownership) {
        setServiceStatus("ownershipData", "ok", `${dataStandLabel(ownership.version)} · ${ownership.records.length.toLocaleString("de-DE")} Einträge · Lokal verfügbar`);
      }
      const activeVision = readCachedActiveVisualLabProfile();
      if (activeVision) {
        const display = visionProductionRuntimeDisplay(activeVision);
        const compatibility = visionRuntimeCompatibility(activeVision.production_bundle);
        const label = display.recognitionRevision ? `Stand ${display.recognitionRevision}` : activeVision.production_bundle ? "Optimierter Stand" : "Standard-Erkennung";
        setServiceStatus("visionProduction", activeVision.production_bundle && !compatibility.compatible ? "warning" : "ok", activeVision.production_bundle && !compatibility.compatible ? `Standard-Fallback aktiv · ${label} nicht verwendbar` : `${label} · Lokal verfügbar`);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [setServiceStatus]);

  const hasActiveEmailQueue = queueEntries.some(
    (entry) => entry.kind === "email" && (entry.state === "pending" || entry.state === "retrying"),
  );

  const refreshEmailQueueOnlyImpl = useCallback(async (): Promise<void> => {
    if (!protectedRemoteAllowed) return;
    const isAdmin = getLastKnownAuthUser()?.role === "admin";
    try {
      const response = await fetchWithTimeout(
        isAdmin ? "/api/email/admin/queue" : "/api/email/queue",
        { credentials: "include", cache: "no-store" },
        1800,
      );
      if (!response.ok) return;
      const emailEntries = mapEmailQueueEntries(await response.json() as EmailQueueResponseItem[], isAdmin);
      setQueueEntriesState((current) => [
        ...current.filter((entry) => entry.kind !== "email"),
        ...emailEntries,
      ]);
    } catch {
      // Der letzte autoritative E-Mail-Queue-Stand bleibt sichtbar; der Fast-Watch
      // versucht es mit Backoff erneut und löscht pending nicht aufgrund eines
      // bloßen Transportfehlers.
    }
  }, [protectedRemoteAllowed]);
  const refreshEmailQueueOnly = useSingleFlightTask(refreshEmailQueueOnlyImpl);

  useEffect(() => {
    if (!protectedRemoteAllowed || !hasActiveEmailQueue) return;

    // Solange wirklich ein E-Mail-Versand offen ist, gehört die Queue-Wahrheit in
    // den globalen AppStatus und nicht in die Lebensdauer der Abschlussseite.
    // Der leichte Fast-Watcher läuft nur während pending/retrying, pausiert im
    // Hintergrund und beendet sich automatisch mit der ersten autoritativen
    // Queue-Antwort ohne offenen Versand.
    const poller = createSequentialPoller({
      intervalMs: 1_500,
      initialDelayMs: 600,
      pauseWhenHidden: true,
      errorBackoffMultiplier: 2,
      maxErrorIntervalMs: 12_000,
      request: async () => { await refreshEmailQueueOnly(); return null; },
    });
    poller.start();
    return () => poller.stop();
  }, [hasActiveEmailQueue, protectedRemoteAllowed, refreshEmailQueueOnly]);

  useEffect(() => {
    // Statusprüfung kurz nach dem ersten Paint starten. request→complete→delay verhindert
    // überlappende Status-Requests bei langsamen VPN-/Backend-Verbindungen.
    const poller = createSequentialPoller({
      // Pro Tab stabiler Jitter entzerrt gleichzeitiges Wake-up vieler Geräte.
      intervalMs: 27_500 + Math.floor(Math.random() * 5_001),
      initialDelayMs: 350 + Math.floor(Math.random() * 551),
      pauseWhenHidden: true,
      errorBackoffMultiplier: 2,
      maxErrorIntervalMs: 120_000,
      request: async () => { await refreshSystemStatus(); return null; },
    });
    const refresh = () => poller.trigger();
    poller.start();
    window.addEventListener("materialentnahme:status-refresh", refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", refresh);
    return () => {
      poller.stop();
      window.removeEventListener("materialentnahme:status-refresh", refresh);
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", refresh);
    };
  }, [refreshSystemStatus]);

  const value = useMemo<AppStatusContextValue>(() => ({
    activity,
    issues,
    services,
    queueEntries,
    visionOptimizer,
    setActivity,
    reportIssue,
    clearIssue,
    clearAllIssues,
    setServiceStatus,
    setQueueEntries,
    refreshSystemStatus,
  }), [
    activity,
    issues,
    services,
    queueEntries,
    visionOptimizer,
    setActivity,
    reportIssue,
    clearIssue,
    clearAllIssues,
    setServiceStatus,
    setQueueEntries,
    refreshSystemStatus,
  ]);

  return <AppStatusContext.Provider value={value}>{children}</AppStatusContext.Provider>;
}
